import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { ErrorState, Loading } from "@lopie/kernel-ui";

import {
  blankRecord,
  ceilingTags,
  changed,
  followHead,
  inputKind,
  offeredTypes,
  personaRows,
  personasBasis,
  recordRows,
  refusalTarget,
  reviseBody,
  rowKey,
  testUserLine,
} from "./sandbox.js";

/**
 * Who tests the app, with what, and where it may call: the three inputs a draft
 * carries for its sandbox (LO-175), edited without the agent (LO-189).
 *
 * **Everything here is drawn from the kernel.** Persona tags are the tags the
 * draft's own reads name, never a list written here, so a persona outside the
 * ceiling cannot be built on this screen. Sample-record forms are drawn from
 * `GET /schema` field by field, so a type the kernel adds is editable with no
 * change to this file. The one rule not copied is every rule about what is
 * *allowed*: the kernel's refusal is shown in its own words, naming the persona,
 * the record or the host, because a second copy of those rules would be wrong
 * the first time the real ones change.
 *
 * **Saving replaces the draft.** The kernel returns a new draft with a new id
 * and closes the old one as replaced; nothing is sent for approval. That is said
 * where the button is, because "Save" would otherwise read as editing in place.
 */

const HELD = { "overflowWrap": "anywhere", minWidth: 0 };

/** A short word for what a state means to the author. */
const STATE_WORD = {
  draft: "draft, not sent", pending: "waiting for approval", approved: "approved",
  rejected: "rejected", superseded: "replaced", cancelled: "cancelled",
};

/** A refusal is information: the kernel's sentence whole, then the structured
 *  half of the same refusal so nobody has to parse a sentence to find the row. */
function Refused({ failure }) {
  const b = failure?.body || {};
  const list = (v) => (Array.isArray(v) ? v.join(", ") : v);
  const named = [
    ["test user", b.persona],
    ["type", b.type],
    ["record", Number.isInteger(b.index) ? `${b.index + 1} of ${b.type}` : ""],
    ["tags", list(b.tags)],
    ["groups", list(b.groups)],
    ["tags it asks to read", list(b.ceiling_tags)],
    ["types it asks for", list(b.requested_types)],
  ].filter(([, v]) => v !== undefined && v !== null && v !== "");
  return (
    <Alert severity="error" variant="outlined" role="alert">
      <AlertTitle>Not saved: the kernel refused this</AlertTitle>
      <Typography variant="body2" sx={HELD}>{failure.message}</Typography>
      {named.length ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ...HELD }}>
          {named.map(([key, value], i) => (
            <Box component="span" key={key}>
              {i ? " · " : ""}{key}: <code>{value}</code>
            </Box>
          ))}
        </Typography>
      ) : null}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        The draft is exactly as it was. Nothing was replaced.
      </Typography>
    </Alert>
  );
}

/** One thing the kernel refused, said again at the row it is about. A word as
 *  well as the colour, so it reads without either. */
function RefusedMark({ children = "Refused" }) {
  return (
    <Chip size="small" variant="outlined" color="error" label={children}
      sx={{ fontWeight: 600 }} />
  );
}

function Section({ title, basis, children, count }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }}>
      <Stack spacing={1.25}>
        <Typography variant="overline" color="text.secondary">
          {title}{count !== undefined ? ` · ${count}` : ""}
        </Typography>
        {children}
        {basis ? (
          <Typography variant="caption" color="text.secondary">{basis}</Typography>
        ) : null}
      </Stack>
    </Paper>
  );
}

/** Pick any of `options`. A selected one is filled and ticked, so the state
 *  reads without colour; each is a real button, reachable by keyboard. */
function Choices({ options, chosen, onToggle, disabled, label, mark }) {
  return (
    <Stack direction="row" role="group" aria-label={label}
      sx={{ gap: 1, flexWrap: "wrap" }}>
      {options.map((option) => {
        const on = chosen.includes(option);
        return (
          <Chip key={option} size="small" clickable disabled={disabled}
            aria-pressed={on}
            variant={on ? "filled" : "outlined"}
            color={on ? "primary" : "default"}
            label={`${on ? "✓ " : ""}${option}${mark?.(option) ? ` (${mark(option)})` : ""}`}
            onClick={() => onToggle(option)} />
        );
      })}
    </Stack>
  );
}

function EgressEditor({ hosts, onChange, disabled, refused }) {
  const [text, setText] = useState("");
  const [said, setSaid] = useState("");
  const add = () => {
    const host = text.trim().toLowerCase();
    if (!host) return;
    if (hosts.includes(host)) {
      setSaid(`${host} is already listed.`);
      return;
    }
    setSaid("");
    onChange([...hosts, host]);
    setText("");
  };
  return (
    <Section
      title="Outside sites the app may call"
      count={hosts.length}
      basis="Exact site names, lowercase: no wildcard, scheme, port, path or IP address. Part of what is approved, and what a real install is held to; a reviewer can strike a site and can never add one."
    >
      {hosts.length ? (
        <Stack component="ul" spacing={0.75} sx={{ m: 0, p: 0, listStyle: "none" }}>
          {hosts.map((host) => (
            <Stack component="li" key={host} direction="row" spacing={1}
              sx={{ alignItems: "center", flexWrap: "wrap" }}>
              <code style={{ overflowWrap: "anywhere" }}>{host}</code>
              {refused.hosts.includes(host) ? <RefusedMark>Named in the refusal</RefusedMark> : null}
              <Box sx={{ flex: 1 }} />
              <Button size="small" disabled={disabled}
                aria-label={`Remove site ${host}`}
                onClick={() => onChange(hosts.filter((h) => h !== host))}>
                Remove
              </Button>
            </Stack>
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No outside sites. The app is asking to contact none.
        </Typography>
      )}
      <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start", flexWrap: "wrap" }}>
        <TextField size="small" label="Add an outside site" placeholder="api.example.com"
          value={text} disabled={disabled}
          error={Boolean(said)} helperText={said || " "}
          onChange={(e) => { setText(e.target.value); setSaid(""); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          sx={{ minWidth: 240, flex: 1 }} />
        <Button variant="outlined" disabled={disabled || !text.trim()} onClick={add}
          sx={{ mt: 0.25 }}>
          Add site
        </Button>
      </Stack>
    </Section>
  );
}

function PersonaEditor({ personas, onChange, tags, disabled, refused }) {
  const update = (key, patch) =>
    onChange(personas.map((p) => (p.key === key ? { ...p, ...patch } : p)));
  return (
    <Section
      title="Test users"
      count={personas.length}
      basis={personasBasis(tags)}
    >
      {personas.map((p, i) => (
        <Paper key={p.key} variant="outlined"
          sx={{ p: 1.5, minWidth: 0, borderColor: refused.persona && refused.persona === p.name.trim() ? "error.main" : undefined }}>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start", flexWrap: "wrap" }}>
              <TextField size="small" label="Name" value={p.name} disabled={disabled}
                onChange={(e) => update(p.key, { name: e.target.value })}
                slotProps={{ htmlInput: { "aria-label": `Name of test user ${i + 1}`, maxLength: 64 } }}
                sx={{ minWidth: 200, flex: 1 }} />
              {refused.persona && refused.persona === p.name.trim() ? <RefusedMark /> : null}
              <Button size="small" disabled={disabled} sx={{ mt: 0.5 }}
                aria-label={`Remove test user ${p.name || i + 1}`}
                onClick={() => onChange(personas.filter((x) => x.key !== p.key))}>
                Remove
              </Button>
            </Stack>
            {tags.length ? (
              <Choices options={tags} chosen={p.tags} disabled={disabled}
                label={`Tags for test user ${p.name || i + 1}`}
                onToggle={(tag) => update(p.key, {
                  tags: p.tags.includes(tag) ? p.tags.filter((t) => t !== tag) : [...p.tags, tag].sort(),
                })} />
            ) : null}
            <Typography variant="caption" color="text.secondary">
              {testUserLine(p.tags, tags).text}
            </Typography>
          </Stack>
        </Paper>
      ))}
      <Box>
        <Button variant="outlined" disabled={disabled}
          onClick={() => onChange([...personas, { key: rowKey("persona"), name: "", tags: [] }])}>
          Add a test user
        </Button>
      </Box>
    </Section>
  );
}

/** One schema field as a control, the kind of control chosen from what the
 *  kernel says the field is. */
function FieldInput({ field, value, onChange, error, disabled, label }) {
  const kind = inputKind(field);
  const hints = [
    field.required ? "required" : "",
    field.description,
    field.format && field.format !== "enum" ? `format: ${field.format}` : "",
    field.type === "timestamp" ? "ISO 8601, e.g. 2026-01-31T09:00:00Z" : "",
    field.minor_units
      ? `whole number in minor units (cents)${field.currency_field ? `; currency is ${field.currency_field}` : ""}`
      : "",
    field.minimum !== undefined || field.maximum !== undefined
      ? `between ${field.minimum ?? "any"} and ${field.maximum ?? "any"}` : "",
    field.repeated && kind !== "enums" ? "one per line" : "",
    kind === "json" ? "JSON" : "",
  ].filter(Boolean).join(" · ");
  const common = {
    size: "small", fullWidth: true, disabled,
    label: `${field.name}${field.required ? " *" : ""}`,
    error: Boolean(error),
    helperText: error ? `Not ${error}` : hints || " ",
  };
  if (kind === "enum") {
    return (
      <TextField {...common} select value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        slotProps={{ select: { native: true }, htmlInput: { "aria-label": label }, inputLabel: { shrink: true } }}>
        <option value="">(none)</option>
        {field.enum.map((option) => <option key={option} value={option}>{option}</option>)}
      </TextField>
    );
  }
  if (kind === "enums") {
    const chosen = value || [];
    return (
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary">{common.label}</Typography>
        <Choices options={field.enum} chosen={chosen} disabled={disabled} label={label}
          onToggle={(o) => onChange(chosen.includes(o) ? chosen.filter((x) => x !== o) : [...chosen, o])} />
        <Typography variant="caption" color={error ? "error" : "text.secondary"} sx={{ display: "block" }}>
          {error ? `Not ${error}` : hints}
        </Typography>
      </Box>
    );
  }
  const multiline = kind === "json" || kind === "lines" || kind === "integers";
  return (
    <TextField {...common} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
      multiline={multiline} minRows={multiline ? 2 : undefined}
      sx={kind === "json" ? { "& textarea": { fontFamily: "monospace" } } : undefined}
      slotProps={{
        htmlInput: {
          "aria-label": label,
          ...(kind === "integer" ? { inputMode: "numeric" } : {}),
          ...(kind === "json" ? { spellCheck: false } : {}),
        },
      }} />
  );
}

function RecordEditor({ row, index, spec, open, onToggle, onChange, onRemove, ceiling, errors, disabled, refused }) {
  const panel = useId();
  const named = spec?.label ? row.values?.[spec.label] : "";
  const title = `${row.type} ${index}`;
  const isRefused = refused.record === row.key;
  if (!spec) {
    return (
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
          <Typography variant="body2" sx={HELD}>
            {title}: {row.type} is not in <code>/schema</code> any more, so this record is
            kept exactly as it was and cannot be edited here.
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button size="small" disabled={disabled} onClick={onRemove}
            aria-label={`Remove ${title}`}>Remove</Button>
        </Stack>
      </Paper>
    );
  }
  return (
    <Paper variant="outlined"
      sx={{ p: 1.5, minWidth: 0, borderColor: isRefused ? "error.main" : undefined }}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
          <Button size="small" aria-expanded={open} aria-controls={panel} onClick={onToggle}
            sx={{ textTransform: "none", justifyContent: "flex-start", textAlign: "left", ...HELD }}>
            {open ? "▾" : "▸"} {title}{row.id ? ` · ${row.id}` : ""}{named ? ` · ${named}` : ""}
          </Button>
          {isRefused ? <RefusedMark /> : null}
          {Object.keys(errors || {}).length ? <RefusedMark>Needs fixing</RefusedMark> : null}
          <Box sx={{ flex: 1 }} />
          <Button size="small" disabled={disabled} onClick={onRemove}
            aria-label={`Remove ${title}`}>
            Remove
          </Button>
        </Stack>
        {open ? (
          <Stack id={panel} spacing={1.5}>
            <TextField size="small" label="id (optional)" value={row.id} disabled={disabled}
              onChange={(e) => onChange({ id: e.target.value })}
              helperText="Names this record within its type. Left out, the kernel numbers it by position."
              slotProps={{ htmlInput: { "aria-label": `${title} id` } }} sx={{ maxWidth: 360 }} />
            {spec.tags?.length ? (
              <Box>
                <Typography variant="caption" color="text.secondary">Tags on this record</Typography>
                <Choices options={spec.tags} chosen={row.tags} disabled={disabled}
                  label={`Tags on ${title}`}
                  mark={(t) => (ceiling.includes(t) ? "" : "outside what it asks for")}
                  onToggle={(tag) => onChange({
                    tags: row.tags.includes(tag) ? row.tags.filter((t) => t !== tag) : [...row.tags, tag].sort(),
                  })} />
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  A tag outside what the app asks to read is how a test copy shows the app is
                  not given what it should not see.
                </Typography>
              </Box>
            ) : null}
            <Box sx={{
              display: "grid", gap: 1.5, minWidth: 0,
              gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" },
            }}>
              {(spec.fields || []).map((field) => (
                <FieldInput key={field.name} field={field} label={`${title} ${field.name}`}
                  value={row.values[field.name]} disabled={disabled}
                  error={errors?.[field.name]}
                  onChange={(value) => onChange({ values: { ...row.values, [field.name]: value } })} />
              ))}
            </Box>
          </Stack>
        ) : null}
      </Stack>
    </Paper>
  );
}

function RecordsEditor({ rows, onChange, schema, request, ceiling, errors, disabled, refused }) {
  const types = useMemo(() => offeredTypes(schema, request), [schema, request]);
  const [adding, setAdding] = useState("");
  const [open, setOpen] = useState(() => new Set());
  const chosen = adding || types[0]?.type || "";
  const specs = new Map(types.map((t) => [t.type, t]));
  // The n-th record of its own type, which is what the kernel's refusal counts.
  const seen = {};
  const numbered = rows.map((row) => {
    seen[row.type] = (seen[row.type] || 0) + 1;
    return { row, index: seen[row.type] };
  });
  const patch = (key, change) => onChange(rows.map((r) => (r.key === key ? { ...r, ...change } : r)));
  const add = () => {
    const spec = specs.get(chosen);
    if (!spec) return;
    const row = blankRecord(spec);
    onChange([...rows, row]);
    setOpen((was) => new Set(was).add(row.key));
  };
  return (
    <Section
      title="Sample records"
      count={rows.length}
      basis="Made-up records the test copy starts with, checked by the same rule a real record meets. They are never part of what is approved, and a pass against them says the app behaved for these records, not for yours."
    >
      {numbered.map(({ row, index }) => (
        <RecordEditor key={row.key} row={row} index={index} spec={specs.get(row.type)}
          open={open.has(row.key)} disabled={disabled} ceiling={ceiling}
          errors={errors[row.key]} refused={refused}
          onToggle={() => setOpen((was) => {
            const next = new Set(was);
            if (next.has(row.key)) next.delete(row.key); else next.add(row.key);
            return next;
          })}
          onChange={(change) => patch(row.key, change)}
          onRemove={() => onChange(rows.filter((r) => r.key !== row.key))} />
      ))}
      {!rows.length ? (
        <Typography variant="body2" color="text.secondary">
          No sample records. The test copy is made from the data model alone.
        </Typography>
      ) : null}
      <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start", flexWrap: "wrap" }}>
        <TextField select size="small" label="Type" value={chosen} disabled={disabled || !types.length}
          onChange={(e) => setAdding(e.target.value)}
          slotProps={{ select: { native: true }, htmlInput: { "aria-label": "Type of the new sample record" } }}
          sx={{ minWidth: 220 }}>
          {types.map((t) => (
            <option key={t.type} value={t.type}>{t.type}{t.requested ? " (requested)" : ""}</option>
          ))}
        </TextField>
        <Button variant="outlined" disabled={disabled || !chosen} onClick={add} sx={{ mt: 0.25 }}>
          Add a sample record
        </Button>
      </Stack>
    </Section>
  );
}

/** What a sent-for-approval or decided request carries, read-only: the same
 *  three inputs, with nothing to press. */
function ReadOnly({ request, schema }) {
  const { egress = [], personas = [], fixtures = {} } = request.proposed || {};
  const counts = Object.entries(fixtures).map(([type, rows]) => `${type} ${rows.length}`);
  return (
    <Stack spacing={1}>
      <Typography variant="body2">
        Outside sites: {egress.length ? egress.join(", ") : "none"}.
      </Typography>
      <Typography variant="body2">
        Test users: {personas.length
          ? personas.map((p) => `${p.name} (${p.tags.length ? p.tags.join(", ") : "no tags"})`).join("; ")
          : "none"}.
      </Typography>
      <Typography variant="body2">
        Sample records: {counts.length ? counts.join(", ") : "none"}
        {schema ? "" : " (the data model could not be read)"}.
      </Typography>
    </Stack>
  );
}

/**
 * The editor. `requestId` is the draft to edit; if it has been replaced since,
 * the newest one is found and edited instead, so a link or a chat holding an old
 * id lands on the draft that is current.
 */
export default function SandboxInputs({ get, post, requestId, onReplaced }) {
  const [head, setHead] = useState(null);
  const [schema, setSchema] = useState(null);
  const [loadFailure, setLoadFailure] = useState(null);
  const [egress, setEgress] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [records, setRecords] = useState([]);
  const [held, setHeld] = useState(null);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState(null);
  const [positions, setPositions] = useState({});
  const [saved, setSaved] = useState(null);
  const [retry, setRetry] = useState(0);

  // The request this editor holds. A save hands the parent a new id to show,
  // and the editor already has that request: reading it again would blank the
  // screen on the way to what it is already showing.
  const holding = useRef("");
  useEffect(() => {
    if (holding.current === requestId) return undefined;
    let live = true;
    setHead(null);
    setLoadFailure(null);
    followHead((id) => get(`/manifest-requests/${id}`), requestId)
      .then((found) => {
        if (!live) return;
        holding.current = found.request_id;
        setHead(found);
      })
      .catch((e) => live && setLoadFailure(e));
    return () => { live = false; };
  }, [get, requestId, retry]);
  useEffect(() => {
    let live = true;
    get("/schema").then((s) => live && setSchema(s)).catch((e) => live && setLoadFailure(e));
    return () => { live = false; };
  }, [get, retry]);

  // What is on screen starts as what the kernel holds, and is put back to it
  // whenever the kernel's version changes (a load, or a save).
  const reset = useCallback((request, types) => {
    const proposed = request.proposed || {};
    const rows = recordRows(types, proposed.fixtures);
    const next = {
      egress: [...(proposed.egress || [])],
      personas: personaRows(proposed.personas),
      records: rows,
    };
    setEgress(next.egress);
    setPersonas(next.personas);
    setRecords(next.records);
    setHeld(reviseBody({ ...next, schema: types }).body);
    setFailure(null);
  }, []);
  useEffect(() => {
    if (head && schema) reset(head, schema);
  }, [head, schema, reset]);

  const ceiling = useMemo(() => ceilingTags(head), [head]);
  const draft = head?.state === "draft";
  const shaped = useMemo(
    () => reviseBody({ egress, personas, records, schema }),
    [egress, personas, records, schema]);
  const isChanged = Boolean(held) && changed(held, shaped.body);
  const stuck = Object.keys(shaped.errors).length;
  const refused = useMemo(
    () => refusalTarget(failure, { positions, egress }),
    [failure, positions, egress]);

  const save = async () => {
    setSaving(true);
    setFailure(null);
    setSaved(null);
    setPositions(shaped.positions);
    try {
      const made = await post(`/manifest-requests/${head.request_id}/revise`, shaped.body);
      setSaved({ id: made.request_id, replaced: made.replaces });
      holding.current = made.request_id;
      setHead(made.request);
      onReplaced?.(made);
    } catch (e) {
      setFailure(e);
    } finally {
      setSaving(false);
    }
  };

  if (loadFailure) {
    return (
      <ErrorState failure={loadFailure} title="The test settings could not be read"
        onRetry={() => { holding.current = ""; setRetry((n) => n + 1); }} />
    );
  }
  if (!head || !schema) return <Loading label="Reading the test settings" minHeight={80} />;

  return (
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <Chip size="small" variant="outlined" label={STATE_WORD[head.state] || head.state} />
        <Typography variant="caption" color="text.secondary" sx={HELD}>
          Request <code>{head.request_id}</code>
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        These are what the app is tried with before anybody is asked to approve
        it: who signs in, what data is there, and which outside sites it may
        contact. Only the outside sites are part of what gets approved; the test
        users and sample records are for the test copy alone.
      </Typography>

      {!draft ? (
        <>
          <Alert severity="info" variant="outlined">
            Only a draft can be edited, and this request is {STATE_WORD[head.state] || head.state}.
            To change what was sent for approval, ask the builder for a change.
          </Alert>
          <ReadOnly request={head} schema={schema} />
        </>
      ) : (
        <>
          <EgressEditor hosts={egress} onChange={setEgress} disabled={saving} refused={refused} />
          <PersonaEditor personas={personas} onChange={setPersonas} tags={ceiling}
            disabled={saving} refused={refused} />
          <RecordsEditor rows={records} onChange={setRecords} schema={schema} request={head}
            ceiling={ceiling} errors={shaped.errors} disabled={saving} refused={refused} />

          {failure ? (
            failure.status >= 400 && failure.status < 500 && failure.body?.error
              ? <Refused failure={failure} />
              : <ErrorState failure={failure} title="Not saved" />
          ) : null}
          {saved ? (
            <Alert severity="success" variant="outlined" role="status">
              <AlertTitle>Saved as a new draft</AlertTitle>
              <Typography variant="body2" sx={HELD}>
                <code>{saved.id}</code> replaces <code>{saved.replaced}</code>. Nothing
                was sent for approval.
              </Typography>
            </Alert>
          ) : null}

          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", flexWrap: "wrap" }}>
            <Button variant="contained" disabled={!isChanged || saving || stuck > 0} onClick={save}>
              {saving ? "Saving…" : "Save as a new draft"}
            </Button>
            <Button disabled={!isChanged || saving} onClick={() => reset(head, schema)}>
              Discard changes
            </Button>
            <Typography variant="body2" color="text.secondary" role="status">
              {stuck
                ? `${stuck} ${stuck === 1 ? "record needs" : "records need"} fixing before it can be saved.`
                : isChanged ? "Unsaved changes." : "No unsaved changes."}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Saving does not edit this draft: it makes a new one that replaces it, and
            the test copy of the old draft goes with it. Nothing is sent for approval
            until you send it.
          </Typography>
        </>
      )}
    </Stack>
  );
}
